/* eslint-disable jsx-a11y/alt-text */
// @ts-nocheck
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  Suspense
} from 'react'
import Cookies from 'universal-cookie'
import { useAccount } from 'wagmi'
import { readContract } from '@wagmi/core'
import '../App.css'
import MultiCallAbi from '../config/MultiCallAbi.json'
import '../styles/MainContainer.css'
import Footer from '../components/Footer.jsx'
import { toast } from 'react-hot-toast'
import { ReactComponent as EditIcon } from '../icons/edit.svg'
import TopBar from '../components/TopBar.jsx'
import ProfileUploadBox from '../components/ProfileUploadBox.jsx'
import ClipLoader from 'react-spinners/ClipLoader'
import { StringParam, useQueryParam } from 'use-query-params'
import {
  imageUrl,
  apiUrl,
  imageUploadUrl,
  ethPriceApiUrl,
  registryChainIds,
  chainLogos,
  scanLinks,
  coinNames,
  chainNames
} from '../utils/constants.ts'
import TokenLogoImage from '../components/TokenLogoImage.jsx'
import { getMulticallAddress, getPrimaryDeployment } from '../utils/addressHelpers.ts'
import buildBuyLink from '../utils/buildBuyLink.ts'
import rot13 from '../../utils/encode.ts'
import CopyIcon from '../icons/copy.svg'
import { Link } from 'react-router-dom'
import { wagmiAdapter } from '../App.jsx'
import { fetchDevAddress } from '../utils/pumpMetadata.ts'

// Exclude chains that cause RPC/CORS issues in the browser (Profile page only)
const activeChainIds = (
  Array.isArray(registryChainIds) ? registryChainIds : []
).filter((id: number) => id !== 964)

// --- API path helper: always uses absolute API URL (avoids dev index.html fallback) ---
const apiPath = (path: string) => {
  const isLocal =
    typeof window !== 'undefined' &&
    window.location &&
    window.location.hostname === 'localhost'
  // In dev, prefer absolute API to avoid SPA index.html when proxy is not configured
  const base = String(apiUrl || '').replace(/\/$/, '')
  return `${base}${path.startsWith('/') ? path : '/' + path}`
}

const TradeCard = React.lazy(() => import('../components/TradeCard.jsx'))
const BalanceCard = React.lazy(() => import('../components/BalanceCard.jsx'))

/** -------------------- Reusable UI Skeletons (perceived perf) -------------------- */
const SkeletonLine = ({ className = '' }: { className?: string }) => (
  <div className={`bg-[#22252f] rounded ${className} animate-pulse`} />
)

const SkeletonAvatar = ({ size = 40 }: { size?: number }) => (
  <div
    className="bg-[#22252f] rounded-lg animate-pulse"
    style={{ width: size, height: size, borderRadius: 12 }}
  />
)

const BalanceRowSkeleton = () => (
  <div
    className="rounded-lg border border-[#2a2d36] bg-[#171a21] p-3 flex items-center gap-3"
    role="status"
    aria-busy="true"
  >
    <SkeletonAvatar size={40} />
    <div className="flex-1 min-w-0">
      <SkeletonLine className="h-3 w-1/3 mb-2" />
      <SkeletonLine className="h-3 w-1/4" />
    </div>
    <SkeletonLine className="h-4 w-16" />
  </div>
)

const TradeRowSkeleton = () => (
  <div
    className="rounded-lg border border-[#2a2d36] bg-[#171a21] p-3"
    role="status"
    aria-busy="true"
  >
    <div className="grid grid-cols-4 gap-2 items-center">
      <SkeletonLine className="h-4 w-24" />
      <SkeletonLine className="h-4 w-16" />
      <SkeletonLine className="h-4 w-20" />
      <SkeletonLine className="h-4 w-16" />
    </div>
  </div>
)

const CoinCardSkeleton = () => (
  <div
    className="claim-card w-full p-4 sm:p-6 bg-[#171a21] border border-[#2a2d36] rounded-lg"
    role="status"
    aria-busy="true"
  >
    <div className="grid grid-cols-1 md:grid-cols-[72px_minmax(0,1fr)_minmax(0,260px)] gap-3 sm:gap-4 items-start">
      {/* Col 1: avatar */}
      <div className="flex md:block justify-start">
        <div className="rounded-lg bg-[#22252f] w-[52px] h-[52px] sm:w-[64px] sm:h-[64px] animate-pulse" />
      </div>

      {/* Col 2: details (title + chips + desc + socials) */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="h-5 w-2/5 bg-[#22252f] rounded animate-pulse" />
        <div className="flex items-center gap-2">
          <div className="h-4 w-20 bg-[#22252f] rounded animate-pulse" />
          <div className="h-4 w-16 bg-[#22252f] rounded animate-pulse" />
        </div>
        <div className="space-y-1.5">
          <div className="h-3 w-3/4 bg-[#22252f] rounded animate-pulse" />
          <div className="h-3 w-2/3 bg-[#22252f] rounded animate-pulse" />
        </div>
        {/* socials placeholder */}
        <div className="flex items-center gap-3 pt-1">
          <div className="h-4 w-14 bg-[#22252f] rounded animate-pulse" />
          <div className="h-4 w-8 bg-[#22252f] rounded animate-pulse" />
          <div className="h-4 w-8 bg-[#22252f] rounded animate-pulse" />
        </div>
      </div>

      {/* Col 3: metrics & actions */}
      <div className="flex flex-col gap-3 md:items-end">
        <div className="rounded-lg bg-[#1d2028] px-3 py-2 w-full md:w-[200px]">
          <div className="h-3 w-16 bg-[#22252f] rounded animate-pulse mb-1" />
          <div className="h-4 w-24 bg-[#22252f] rounded animate-pulse" />
        </div>
        <div className="flex flex-wrap md:justify-end items-center gap-2">
          <div className="h-8 w-28 bg-[#22252f] rounded-lg animate-pulse" />
          <div className="h-8 w-32 bg-[#22252f] rounded-lg animate-pulse" />
        </div>
      </div>
    </div>
  </div>
)

/** ---------- lightweight SWR-style cache (stale-while-revalidate) ---------- */
const memoryCache = new Map<string, { t: number; d: any }>()
const CACHE_TTL_MS = 20 * 1000 // 20s TTL

const now = () => Date.now()

const getCache = (key: string) => {
  try {
    const mem = memoryCache.get(key)
    if (mem && now() - mem.t < CACHE_TTL_MS) return mem.d
    const raw = localStorage.getItem(key)
    if (!raw) return undefined
    const parsed = JSON.parse(raw)
    if (parsed && parsed.t && now() - parsed.t < CACHE_TTL_MS) {
      memoryCache.set(key, { t: parsed.t, d: parsed.d })
      return parsed.d
    }
  } catch {}
  return undefined
}

const setCache = (key: string, data: any) => {
  const entry = { t: now(), d: data }
  memoryCache.set(key, entry)
  try {
    localStorage.setItem(key, JSON.stringify(entry))
  } catch {}
}

const withCache = async (
  key: string,
  fetcher: () => Promise<any>,
  onData: (d: any) => void,
  { revalidate = true } = {}
) => {
  const cached = getCache(key)
  if (cached !== undefined) onData(cached) // serve stale immediately
  if (!revalidate) return cached
  try {
    const fresh = await fetcher()
    const isEmpty =
      fresh == null ||
      (Array.isArray(fresh)
        ? fresh.length === 0
        : typeof fresh === 'object'
        ? Object.keys(fresh).length === 0
        : false)
    // Only persist to cache if non-empty; always deliver fresh to UI.
    if (!isEmpty) setCache(key, fresh)
    if (!shallowEqual(cached, fresh)) {
      onData(fresh)
    }
    return fresh
  } catch {
    return cached
  }
}

// ----------- Shallow compare and fetch helpers -------------
const shallowEqual = (a: any, b: any) => {
  if (a === b) return true
  if (!a || !b) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      const ai = a[i]
      const bi = b[i]
      if (typeof ai === 'object' || typeof bi === 'object') {
        if (JSON.stringify(ai) !== JSON.stringify(bi)) return false
      } else if (ai !== bi) return false
    }
    return true
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a)
    const bk = Object.keys(b)
    if (ak.length !== bk.length) return false
    for (const k of ak) {
      const av = (a as any)[k]
      const bv = (b as any)[k]
      if (typeof av === 'object' || typeof bv === 'object') {
        if (JSON.stringify(av) !== JSON.stringify(bv)) return false
      } else if (av !== bv) return false
    }
    return true
  }
  return false
}

const fetchJSON = async (
  url: string,
  init?: RequestInit & { signal?: AbortSignal },
  retries = 2
) => {
  try {
    const res = await fetch(url, init)
    const ct = (res.headers.get('content-type') || '').toLowerCase()

    if (!res.ok) {
      let bodySnippet = ''
      try {
        // Prefer text to avoid JSON parse errors on HTML error pages
        bodySnippet = await res.text()
      } catch {}
      const msg = `Network error ${res.status}${
        res.statusText ? `: ${res.statusText}` : ''
      }`
      if (bodySnippet) {
        const snippet = bodySnippet.replace(/\s+/g, ' ').slice(0, 180)
        toastOnce('error', `${msg} — ${snippet}`)
      } else {
        toastOnce('error', msg)
      }
      throw Object.assign(new Error(msg), { status: res.status })
    }

    // Happy path: JSON content
    if (ct.includes('application/json')) {
      return await res.json()
    }

    // Fallbacks: try to parse as JSON, otherwise raise a friendly error with snippet
    let raw = ''
    try {
      raw = await res.text()
    } catch {}
    if (raw) {
      try {
        return JSON.parse(raw)
      } catch {}
      const snippet = raw.replace(/\s+/g, ' ').slice(0, 180)
      const err = new Error(`Unexpected non-JSON response: ${snippet}`)
      ;(err as any).kind = 'format'
      throw err
    }
    const err = new Error('Empty response')
    ;(err as any).kind = 'empty'
    throw err
  } catch (e) {
    const n = normalizeError(e)
    if (n.kind !== 'abort') toastOnce('error', n.message || 'Request failed')
    if (retries > 0 && n.kind !== 'abort') {
      await new Promise(r => setTimeout(r, 300 * (3 - retries)))
      return fetchJSON(url, init, retries - 1)
    }
    throw e
  }
}

// Dedupe ETH price requests per unique URL
const urlPriceCache = new Map<string, number>()
const getEthPriceByUrl = async (url: string, signal?: AbortSignal) => {
  if (urlPriceCache.has(url)) return urlPriceCache.get(url)!
  const data = await fetchJSON(url, { method: 'GET', signal })
  const price = Number(data?.USD ?? data?.usd ?? data?.price ?? 0)
  urlPriceCache.set(url, price)
  return price
}

// requestIdleCallback polyfill
const ric: (cb: () => void) => void =
  typeof window !== 'undefined' && (window as any).requestIdleCallback
    ? cb => (window as any).requestIdleCallback(cb)
    : cb => setTimeout(cb, 0)

/** -------------------- Error handling & toast helpers -------------------- */
// de‑dupe noisy errors so the user doesn't get spammed
const __toastSeen = new Set<string>()
const toastOnce = (
  type: 'error' | 'success' | 'loading' | 'custom' | 'promise',
  msg: string,
  opts: any = {}
) => {
  const key = `${type}:${msg}`
  if (__toastSeen.has(key)) return
  __toastSeen.add(key)
  try {
    // @ts-ignore – we always have toast imported on this page
    toast[type]
      ? toast[type](msg, { id: key, duration: 4000, ...opts })
      : toast(msg)
  } catch {}
  // allow same message again after a short window
  setTimeout(() => __toastSeen.delete(key), 6000)
}

// classify common failure modes
const normalizeError = (e: any) => {
  if (!e) return { kind: 'unknown', message: 'Unknown error' }
  if (e?.name === 'AbortError')
    return { kind: 'abort', message: 'Request cancelled' }
  if (typeof e === 'string') return { kind: 'message', message: e }
  if (e?.status) return { kind: 'http', message: `HTTP ${e.status}` }
  if (e?.message) return { kind: 'message', message: e.message }
  return { kind: 'unknown', message: 'Something went wrong' }
}

// simple boundary fallback UI piece
const ErrorBanner = ({
  title = 'Something went wrong',
  detail
}: {
  title?: string
  detail?: string
}) => (
  <div
    role="alert"
    className="rounded-lg border border-[#5b1d67] bg-[#2a0f31] text-[#ffd7ff] p-3"
  >
    <div className="font-semibold text-sm">{title}</div>
    {detail && (
      <div className="text-xs opacity-80 mt-0.5 break-words">{detail}</div>
    )}
  </div>
)

const focusNextInList = (container: HTMLElement, dir: 1 | -1) => {
  const tabs = Array.from(
    container.querySelectorAll('[role="tab"]')
  ) as HTMLElement[]
  if (!tabs.length) return
  const active = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true')
  const next = (active + dir + tabs.length) % tabs.length
  tabs[next]?.focus()
}

const Profile = () => {
  useEffect(() => {
    try {
      const origins = new Set<string>()
      const push = (u?: string) => {
        if (!u) return
        try {
          const o = new URL(u).origin
          origins.add(o)
        } catch {}
      }
      // likely hot domains
      push(imageUrl)
      push(apiUrl)
      Object.values(ethPriceApiUrl || {}).forEach(u => push(String(u)))

      origins.forEach(origin => {
        const pre = document.createElement('link')
        pre.rel = 'preconnect'
        pre.href = origin
        pre.crossOrigin = ''
        document.head.appendChild(pre)

        const dns = document.createElement('link')
        dns.rel = 'dns-prefetch'
        dns.href = origin
        document.head.appendChild(dns)
      })

      return () => {
        // no cleanup needed; keep hints cached
      }
    } catch {}
  }, [])
  // Preload lazy chunk modules in background (TradeCard, BalanceCard)
  useEffect(() => {
    const preload = () => {
      try {
        import('../components/TradeCard.jsx')
        import('../components/BalanceCard.jsx')
      } catch {}
    }
    if (typeof (window as any).requestIdleCallback === 'function') {
      ;(window as any).requestIdleCallback(preload)
    } else {
      setTimeout(preload, 0)
    }
  }, [])
  const tabListRef = useRef<HTMLDivElement | null>(null)
  const contentShellRef = useRef<HTMLDivElement | null>(null)
  const [contentMinH, setContentMinH] = useState<number | undefined>(undefined)
  const tabScrollRef = useRef<Record<string, number>>({
    balances: 0,
    trades: 0,
    coins: 0
  })
  const [isSwitching, setIsSwitching] = useState(false)

  let [profileAddress] = useQueryParam('address', StringParam)
  const { address, isConnected } = useAccount()
  const effectiveAddress = profileAddress || address
  const [userName, setUserName] = useState('@hotkey')
  const [userTelegram, setUserTelegram] = useState('')
  const [userX, setUserX] = useState('')
  const [userWebsite, setUserWebsite] = useState('')

  const [logoPreview, setLogoPreview] = useState<string | null>()
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const logoFileInput = useRef<HTMLInputElement>(null)
  let [loading, setLoading] = useState(false)
  const [userAvatarUrl, setUserAvatarUrl] = useState('/img/moonboy67.png')

  const [selectedOption, setSelectedOption] = useState('balances')
  const [loadingBalances, setLoadingBalances] = useState(false)
  const [loadingCoins, setLoadingCoins] = useState(false)
  const [loadingTrades, setLoadingTrades] = useState(false)
  const handleSelect = (value: 'balances' | 'trades' | 'coins') => {
    if (value === selectedOption) return
    const shell = contentShellRef.current
    if (shell) {
      tabScrollRef.current[selectedOption] = shell.scrollTop
      const h = shell.getBoundingClientRect().height
      setContentMinH(h)
    }
    setIsSwitching(true)
    setSelectedOption(value)
  }
  useLayoutEffect(() => {
    if (!isSwitching) return
    const shell = contentShellRef.current
    const id = requestAnimationFrame(() => {
      if (shell) {
        const targetScroll = tabScrollRef.current[selectedOption] ?? 0
        shell.scrollTo({
          top: targetScroll,
          left: 0,
          behavior: 'instant' as any
        })
      }
      setTimeout(() => {
        setContentMinH(undefined)
        setIsSwitching(false)
      }, 250)
    })
    return () => cancelAnimationFrame(id)
  }, [isSwitching, selectedOption])
  const [, setTotalLength] = useState(0)
  const [chadLists, setChadListData] = useState([])
  const [chadBalances, setChadBalances] = useState([])
  const [history, setHistory] = useState([
    { amount: 0, name: '', token: '', type: '', date: '' }
  ])
  const [isProfileCopied, setIsProfileCopied] = useState(false)
  const [isProfileAddrCopied, setIsProfileAddrCopied] = useState(false)
  const [isReferralCopied, setIsReferralCopied] = useState(false)
  const [watchlist, setWatchlist] = useState([])

  const userBannerUrl = '/banner.png'

  const handleEditIconClick = () => {
    setSelectedOption('Details')
  }

  // ----------- Cache warmers for background tab prefetch -------------
  const warmCoinsCache = async (addr: string) => {
    const key = `profile:coins:${String(addr || '').toLowerCase()}`
    return withCache(
      key,
      async () => {
        const allHistory = await fetchJSON(apiPath('/api/getHistoryAll'), {
          method: 'GET'
        })
        const perChain = await Promise.all(
          activeChainIds.map(async chainId => {
            try {
              const deployment = getPrimaryDeployment(chainId)
              const priceUrl = ethPriceApiUrl[chainId]
              const ethPrice = await getEthPriceByUrl(priceUrl)
              const [mainInfo, otherInfo, restInfo]: any = await Promise.all([
                readContract(wagmiAdapter.wagmiConfig, {
                  address: getMulticallAddress(chainId),
                  abi: MultiCallAbi,
                  functionName: 'getUserDeployMainInfo',
                  args: [addr],
                  chainId
                }),
                readContract(wagmiAdapter.wagmiConfig, {
                  address: getMulticallAddress(chainId),
                  abi: MultiCallAbi,
                  functionName: 'getUserDeployOtherInfo',
                  args: [addr],
                  chainId
                }),
                readContract(wagmiAdapter.wagmiConfig, {
                  address: getMulticallAddress(chainId),
                  abi: MultiCallAbi,
                  functionName: 'getRestInfo',
                  chainId
                })
              ])
              const out: any[] = []
              if (mainInfo && otherInfo && mainInfo[0].length > 0) {
                const seen = new Set<string>()
                for (let i = mainInfo[0].length - 1; i >= 0; i--) {
                  const contractAddress = mainInfo[5][i]
                  if (seen.has(contractAddress)) continue
                  seen.add(contractAddress)
                  const tokenPrice = Number(mainInfo[3][i])
                  const marketCap =
                    (tokenPrice * 1_000_000_000 * Number(ethPrice)) / 1e12
                  const website = otherInfo[2][i]
                  const twitter = otherInfo[3][i]
                  const telegram = otherInfo[4][i]
                  const name = otherInfo[0][i]
                  const symbol = otherInfo[1][i]
                  const description = restInfo[2][i]
                  const lpCreated = restInfo[1][i]
                  const logoUrl = `${imageUrl}${contractAddress}-${chainId}-logo.png`
                  const bannerUrl = imageUrl + contractAddress + '-banner.png'
                  const blockchainLogoUrl = chainLogos[chainId]
                  const devAddress = mainInfo[6][i]
                  let lastAction = 'bought',
                    lastActionNumber = 0
                  const filtered = Array.isArray(allHistory)
                    ? allHistory.filter(
                        (it: any) => it.contract === contractAddress
                      )
                    : []
                  if (filtered.length) {
                    const firstType = filtered[0].type
                    let cnt = 0
                    for (const it of filtered) {
                      if (it.type === firstType) cnt++
                      else break
                    }
                    lastAction = firstType
                    lastActionNumber = cnt
                  }
                 out.push({
                  chainId,
                  progress: 0,
                  Liquidity: 0,
                  tokenName: name,
                  tokenSymbol: symbol,
                  tokenDescription: description,
                  logoUrl,
                  bannerUrl,
                  address: mainInfo[5][i],
                  depositedAmount:
                    (Number(mainInfo[4][i]) * Number(ethPrice)) / 1e18,
                  contractAddress,
                  devAddress,
                    dexName: 'hotkeyswap',
                    marketCap,
                    website,
                    twitter,
                    telegram,
                    blockchainLogoUrl,
                    lpCreated,
                    lastAction,
                    lastActionNumber,
                    versionType: deployment?.version || 'v4'
                  })
                }
             }
              if (out.length) {
                const ZERO_ADDRESS =
                  '0x0000000000000000000000000000000000000000'
                const needsCreator = out.filter(entry => {
                  const dev = String(entry.devAddress || '').toLowerCase()
                  return !dev || dev === ZERO_ADDRESS
                })
                if (needsCreator.length) {
                  try {
                    const creators = await Promise.allSettled(
                      needsCreator.map(entry =>
                        fetchDevAddress({
                          wagmiConfig: wagmiAdapter.wagmiConfig,
                          contractAddress: entry.contractAddress,
                          chainId,
                          versionType: entry.versionType,
                          signal: ac.signal
                        })
                      )
                    )
                    const map = new Map()
                    creators.forEach((res, idx) => {
                      if (res.status !== 'fulfilled') return
                      const val = String(res.value || '').toLowerCase()
                      if (!val || val === ZERO_ADDRESS) return
                      const key = needsCreator[idx].contractAddress.toLowerCase()
                      map.set(key, String(res.value))
                    })
                    if (map.size) {
                      out = out.map(entry => {
                        const next = map.get(
                          String(entry.contractAddress).toLowerCase()
                        )
                        return next ? { ...entry, devAddress: next } : entry
                      })
                    }
                  } catch (err) {
                    console.warn('[Profile] creator fallback failed', err)
                  }
                }
              }
              return out
            } catch {
              return []
            }
          })
        )
        return perChain.flat()
      },
      () => {}
    )
  }

  const warmBalancesCache = async (addr: string) => {
    const key = `profile:balances:${String(addr || '').toLowerCase()}`
    return withCache(
      key,
      async () => {
        const perChain = await Promise.all(
          activeChainIds.map(async chainId => {
            try {
              const deployment = getPrimaryDeployment(chainId)
              const mainInfo: any = await readContract(
                wagmiAdapter.wagmiConfig,
                {
                  address: getMulticallAddress(chainId),
                  abi: MultiCallAbi,
                  functionName: 'getUserBalance',
                  args: [addr],
                  chainId
                }
              )
              const rows: any[] = []
              if (mainInfo && mainInfo[0].length > 0) {
                const seen = new Set<string>()
                for (let i = mainInfo[0].length - 1; i >= 0; i--) {
                  const name = mainInfo[0][i]
                  const symbol = mainInfo[1][i]
                  const tokenAddress = mainInfo[2][i]
                  const balance = Number(mainInfo[3][i]) / 1e18
                  const logoUrl = `${imageUrl}${tokenAddress}-${chainId}-logo.png`
                  const k = `${chainId}-${tokenAddress}`
                  if (!seen.has(k)) {
                    seen.add(k)
                    rows.push({
                      chainId,
                      name,
                      symbol,
                      tokenAddress,
                      logoUrl,
                      balance,
                      versionType: deployment?.version || 'v4'
                    })
                  }
                }
              }
              return rows
            } catch {
              return []
            }
          })
        )
        return perChain.flat()
      },
      () => {}
    )
  }

  const warmTradesCache = async (addr: string) => {
    const key = `profile:trades:${String(addr || '').toLowerCase()}`
    return withCache(
      key,
      async () => {
        const data = await fetchJSON(apiPath(`/api/getUserHistory/${addr}`), {
          method: 'GET'
        })
        if (!Array.isArray(data) || data.length === 0) return []
        const historyData: any[] = []
        const seenTrades = new Set<string>()
        for (let i = 0; i < data.length; i++) {
          const it = data[i]
          const chainId = it.chainId
          const amount = it.amount
          const name = it.name
          const token = it.token
          const type = it.type
          const contract = it.contract
          const diff = Date.now() / 1000 - Number(it.timestamp)
          let date: string
          if (diff > 86400) date = `${(diff / 86400).toFixed(0)} days ago`
          else if (diff > 3600) date = `${(diff / 3600).toFixed(0)} hours ago`
          else if (diff > 0) date = `${(diff / 60).toFixed(0)} mins ago`
          else date = ' just now'
          const k = `${chainId}-${contract}-${it.timestamp}-${type}-${amount}`
          if (seenTrades.has(k)) continue
          seenTrades.add(k)
          historyData.push({
            chainId,
            amount,
            name,
            token,
            type,
            contract,
            date
          })
        }
        return historyData
      },
      () => {}
    )
  }
  // --- Abort controllers for tabs
  const controllersRef = useRef<{
    coins: AbortController | null
    balances: AbortController | null
    trades: AbortController | null
  }>({ coins: null, balances: null, trades: null })

  // On mount or tab change, warm caches for non-selected tabs in background
  useEffect(() => {
    if (!effectiveAddress) return
    const run = () => {
      if (selectedOption !== 'coins') warmCoinsCache(effectiveAddress)
      if (selectedOption !== 'balances') warmBalancesCache(effectiveAddress)
      if (selectedOption !== 'trades') warmTradesCache(effectiveAddress)
    }
    if (typeof (window as any).requestIdleCallback === 'function') {
      ;(window as any).requestIdleCallback(run)
    } else {
      setTimeout(run, 0)
    }
  }, [effectiveAddress, selectedOption])

  useEffect(() => {
    const FetchDeployedData = async () => {
      controllersRef.current.coins?.abort()
      const ac = new AbortController()
      controllersRef.current.coins = ac
      try {
        const key = `profile:coins:${String(
          effectiveAddress || ''
        ).toLowerCase()}`
        setLoadingCoins(!getCache(key))
        setChadListData([])
        await withCache(
          key,
          async () => {
            const allHistory = await fetchJSON(apiPath('/api/getHistoryAll'), {
              method: 'GET',
              signal: ac.signal
            })
            const perChain = await Promise.all(
              activeChainIds.map(async chainId => {
                try {
                  const priceUrl = ethPriceApiUrl[chainId]
                  const ethPrice = await getEthPriceByUrl(priceUrl, ac.signal)
                  const [mainInfo, otherInfo, restInfo]: any =
                    await Promise.all([
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: getMulticallAddress(chainId),
                        abi: MultiCallAbi,
                        functionName: 'getUserDeployMainInfo',
                        args: [effectiveAddress],
                        chainId
                      }),
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: getMulticallAddress(chainId),
                        abi: MultiCallAbi,
                        functionName: 'getUserDeployOtherInfo',
                        args: [effectiveAddress],
                        chainId
                      }),
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: getMulticallAddress(chainId),
                        abi: MultiCallAbi,
                        functionName: 'getRestInfo',
                        chainId
                      })
                    ])
                  const out: any[] = []
                  if (mainInfo && otherInfo && mainInfo[0].length > 0) {
                    const seen = new Set<string>()
                    for (let i = mainInfo[0].length - 1; i >= 0; i--) {
                      const contractAddress = mainInfo[5][i]
                      if (seen.has(contractAddress)) continue
                      seen.add(contractAddress)
                      const tokenPrice = Number(mainInfo[3][i])
                      const marketCap =
                        (tokenPrice * 1_000_000_000 * Number(ethPrice)) / 1e12
                      const website = otherInfo[2][i]
                      const twitter = otherInfo[3][i]
                      const telegram = otherInfo[4][i]
                      const name = otherInfo[0][i]
                      const symbol = otherInfo[1][i]
                      const description = restInfo[2][i]
                      const lpCreated = restInfo[1][i]
                      const logoUrl = `${imageUrl}${contractAddress}-${chainId}-logo.png`
                      const bannerUrl =
                        imageUrl + contractAddress + '-banner.png'
                      const blockchainLogoUrl = chainLogos[chainId]
                      const devAddress = mainInfo[6][i]

                      let lastAction = 'bought',
                        lastActionNumber = 0
                      const filtered = Array.isArray(allHistory)
                        ? allHistory.filter(
                            (it: any) => it.contract === contractAddress
                          )
                        : []
                      if (filtered.length) {
                        const firstType = filtered[0].type
                        let cnt = 0
                        for (const it of filtered) {
                          if (it.type === firstType) cnt++
                          else break
                        }
                        lastAction = firstType
                        lastActionNumber = cnt
                      }

                      out.push({
                        chainId,
                        progress: 0,
                        Liquidity: 0,
                        tokenName: name,
                        tokenSymbol: symbol,
                        tokenDescription: description,
                        logoUrl,
                        bannerUrl,
                        address: mainInfo[5][i],
                        depositedAmount:
                          (Number(mainInfo[4][i]) * Number(ethPrice)) / 1e18,
                        contractAddress,
                        devAddress,
                        dexName: 'hotkeyswap',
                        marketCap,
                        website,
                        twitter,
                        telegram,
                        blockchainLogoUrl,
                        lpCreated,
                        lastAction,
                        lastActionNumber
                      })
                    }
                  }
                  return out
                } catch {
                  return []
                }
              })
            )
            return perChain.flat()
          },
          fresh => setChadListData(fresh)
        )
      } catch (e) {
        if ((e as any)?.name !== 'AbortError') {
          console.error(e)
          const n = normalizeError(e)
          toastOnce('error', n.message || 'Failed to load data')
        }
      } finally {
        setLoadingCoins(false)
      }
    }
    const FetchBalances = async () => {
      controllersRef.current.balances?.abort()
      const ac = new AbortController()
      controllersRef.current.balances = ac
      try {
        const key = `profile:balances:${String(
          effectiveAddress || ''
        ).toLowerCase()}`
        setLoadingBalances(!getCache(key))
        setChadBalances([])
        await withCache(
          key,
          async () => {
            const perChain = await Promise.all(
              activeChainIds.map(async chainId => {
                try {
                  const deployment = getPrimaryDeployment(chainId)
                  const mainInfo: any = await readContract(
                    wagmiAdapter.wagmiConfig,
                    {
                      address: getMulticallAddress(chainId),
                      abi: MultiCallAbi,
                      functionName: 'getUserBalance',
                      args: [effectiveAddress],
                      chainId
                    }
                  )
                  const rows: any[] = []
                  if (mainInfo && mainInfo[0].length > 0) {
                    const seen = new Set<string>()
                    for (let i = mainInfo[0].length - 1; i >= 0; i--) {
                      const name = mainInfo[0][i]
                      const symbol = mainInfo[1][i]
                      const tokenAddress = mainInfo[2][i]
                      const balance = Number(mainInfo[3][i]) / 1e18
                      const logoUrl = `${imageUrl}${tokenAddress}-${chainId}-logo.png`
                      const k = `${chainId}-${tokenAddress}`
                      if (!seen.has(k)) {
                        seen.add(k)
                        rows.push({
                          chainId,
                          name,
                          symbol,
                          tokenAddress,
                          logoUrl,
                          balance,
                          versionType: deployment?.version || 'v4'
                        })
                      }
                    }
                  }
                  return rows
                } catch {
                  return []
                }
              })
            )
            return perChain.flat()
          },
          fresh => setChadBalances(fresh)
        )
      } catch (e) {
        if ((e as any)?.name !== 'AbortError') {
          console.error(e)
          const n = normalizeError(e)
          toastOnce('error', n.message || 'Failed to load data')
        }
      } finally {
        setLoadingBalances(false)
      }
    }

    const FetchTrades = async () => {
      const key = `profile:trades:${String(
        effectiveAddress || ''
      ).toLowerCase()}`
      controllersRef.current.trades?.abort()
      const ac = new AbortController()
      controllersRef.current.trades = ac
      setLoadingTrades(!getCache(key))
      try {
        await withCache(
          key,
          async () => {
            const data = await fetchJSON(
              apiPath(`/api/getUserHistory/${effectiveAddress}`),
              { method: 'GET', signal: ac.signal }
            )
            if (!Array.isArray(data) || data.length === 0) return []
            const historyData: any[] = []
            const seenTrades = new Set<string>()
            for (let i = 0; i < data.length; i++) {
              const it = data[i]
              const chainId = it.chainId
              const amount = it.amount
              const buyer = it.buyer
              const name = it.name
              const token = it.token
              const type = it.type
              const contract = it.contract
              const diff = Date.now() / 1000 - Number(it.timestamp)
              let date: string
              if (diff > 86400) date = `${(diff / 86400).toFixed(0)} days ago`
              else if (diff > 3600)
                date = `${(diff / 3600).toFixed(0)} hours ago`
              else if (diff > 0) date = `${(diff / 60).toFixed(0)} mins ago`
              else date = ' just now'
              const k = `${chainId}-${contract}-${it.timestamp}-${type}-${amount}`
              if (seenTrades.has(k)) continue
              seenTrades.add(k)
              historyData.push({
                chainId,
                amount,
                buyer,
                name,
                token,
                type,
                contract,
                date
              })
            }
            return historyData
          },
          fresh =>
            setHistory(
              fresh.length
                ? fresh
                : [{ amount: 0, name: '', token: '', type: '', date: '' }]
            )
        )
      } catch (e) {
        if ((e as any)?.name !== 'AbortError') {
          console.error(e)
          const n = normalizeError(e)
          toastOnce('error', n.message || 'Failed to load data')
        }
      } finally {
        setLoadingTrades(false)
      }
    }
    // subtle loading indicator per tab (auto-dismissed by success/error)
    const toastId = `tabload:${selectedOption}`
    // use loading state of the selected tab to show a quick loader toast
    const isBusy =
      (selectedOption === 'coins' && loadingCoins) ||
      (selectedOption === 'balances' && loadingBalances) ||
      (selectedOption === 'trades' && loadingTrades)
    if (isBusy) {
      try {
        toast.loading('Loading…', { id: toastId, duration: 1500 })
      } catch {}
    }
    // Guard: only fetch if effectiveAddress exists
    if (!effectiveAddress) {
      return
    }
    if (selectedOption === 'coins') {
      FetchDeployedData()
    } else if (selectedOption === 'balances') {
      FetchBalances()
    } else if (selectedOption === 'trades') {
      FetchTrades()
    }
    // Abort all controllers on cleanup
    return () => {
      controllersRef.current.coins?.abort()
      controllersRef.current.balances?.abort()
      controllersRef.current.trades?.abort()
    }
  }, [selectedOption, effectiveAddress])

  // Focus revalidate effect
  useEffect(() => {
    if (!effectiveAddress) return
    const onFocus = () => {
      // Re-warm all caches; the active tab will paint instantly from cache
      warmCoinsCache(effectiveAddress)
      warmBalancesCache(effectiveAddress)
      warmTradesCache(effectiveAddress)
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [effectiveAddress])

  useEffect(() => {
    const storedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || []
    setWatchlist(storedWatchlist)
  }, [])

  useEffect(() => {
    localStorage.setItem('watchlist', JSON.stringify(watchlist))
  }, [watchlist])

  const toggleWatchlist = item => {
    setWatchlist(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    )
  }

  useEffect(() => {
    const FetchInfo = async () => {
      try {
        setUserAvatarUrl(imageUrl + `profile-${effectiveAddress}.png`)
        const data = await fetchJSON(
          apiPath(`/api/getProfile/${effectiveAddress}`),
          { method: 'GET' }
        )
        if (Array.isArray(data) && data.length > 0) {
          setUserName(data[0].name)
          setUserTelegram(data[0].telegram)
          setUserX(data[0].twitter)
          setUserWebsite(data[0].website)
        } else if (data && typeof data === 'object' && data.name) {
          setUserName(data.name)
          setUserTelegram(data.telegram || '')
          setUserX(data.twitter || '')
          setUserWebsite(data.website || '')
        } else {
          setUserName('@HOTKEY')
        }
      } catch (e) {
        console.error(e)
      }
    }
    if (effectiveAddress) {
      FetchInfo()
    }
  }, [effectiveAddress, address])

  const LogoImageUpload = ({ onChange, className, style }) => {
    const handleLogoImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files![0]
      setLogoFile(selectedFile)
      setLogoPreview(URL.createObjectURL(selectedFile))
      onChange(selectedFile)
    }
    const onButtonClick = () => {
      if (logoFileInput.current) {
        logoFileInput.current.click()
      }
    }
    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <input
          type="file"
          ref={logoFileInput}
          accept="image/*"
          onChange={handleLogoImageChange}
          aria-label="Upload profile image"
          style={{ display: 'none' }}
        />
        <ProfileUploadBox
          imageUrl={logoPreview}
          handleClick={onButtonClick}
          className={className}
          style={style}
        />
      </div>
    )
  }

  const [, setImageLogoFile] = useState(null)
  const handleImageLogoChange = file => {
    setImageLogoFile(file)
  }

  const onSave = async () => {
    try {
      setLoading(true)

      let logoUrl
      if (logoFile) {
        setUserAvatarUrl(logoPreview)

        const formData = new FormData()
        formData.append('file', logoFile, address)
        fetch(imageUploadUrl + 'api/profileUploads', {
          method: 'POST',
          body: formData
        })
          .then(async res => {
            logoUrl = await res.json()
            logoUrl = logoUrl.fileInfo.filename
          })
          .catch(error => {
            setLoading(false)
            console.error('Error:', error)
            toastOnce('error', 'Upload failed. Please try again.')
          })
      }
      const sendData = {
        profileAddress: address,
        name: userName,
        telegram: userTelegram,
        twitter: userX,
        website: userWebsite
      }
      const response = await fetch(apiPath('/api/addprofile'), {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json'
        },
        redirect: 'error',
        body: JSON.stringify(sendData)
      })

      if (response.status !== 200) {
        const { error } = await response.json()
        throw new Error(error)
      }
      toast.success(`Updated`)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      const n = normalizeError(err)
      toastOnce('error', n.message || 'Update failed. Please try again later.')
    }
  }

  const BASE_URL = 'https://hotcurves.fun'
  const referlink = address
    ? `${BASE_URL}/?ref=${rot13(address)}`
    : `${BASE_URL}/?ref=`

  const copyAddress = (address, type) => async e => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    const text = String(address ?? '')
    let ok = false
    try {
      // Prefer async Clipboard API when available & secure context
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(text)
        ok = true
      }
    } catch {}
    if (!ok) {
      try {
        const ele = document.createElement('textarea')
        ele.value = text
        ele.setAttribute('readonly', '')
        ele.style.position = 'absolute'
        ele.style.left = '-9999px'
        document.body.appendChild(ele)
        ele.select()
        document.execCommand('copy')
        document.body.removeChild(ele)
        ok = true
      } catch {}
    }
    if (ok) {
      if (type === 'profile') {
        setIsProfileCopied(true)
        setTimeout(() => setIsProfileCopied(false), 1000)
      } else if (type === 'referral') {
        setIsReferralCopied(true)
        setTimeout(() => setIsReferralCopied(false), 1000)
      } else if (type === 'profileTitle') {
        setIsProfileAddrCopied(true)
        setTimeout(() => setIsProfileAddrCopied(false), 1000)
      }
    } else {
      toastOnce('error', 'Copy failed. Long‑press to select manually.')
    }
  }

  // Keep Liquidity in the prop list for compatibility, but do not render it.
  const CoinCard = React.memo(
    ({
      chainId,
      tokenName,
      tokenSymbol,
      tokenDescription,
      logoUrl,
      chadAddress,
      contractAddress,
      devAddress,
      marketCap,
      Liquidity,
      website,
      twitter,
      telegram,
      blockchainLogoUrl,
      lpCreated,
      lastAction,
      lastActionNumber,
      toggleWatchlist,
      watchlist,
      versionType
    }) => {
      const isWatching =
        Array.isArray(watchlist) && watchlist.includes(contractAddress)
      const normAddr = String(chadAddress || contractAddress).toLowerCase()
      const normChain = Number(chainId)
      // --- Per-card dev address copy state ---
      const [isDevCopied, setIsDevCopied] = useState(false)
      const copyDev = async (text: string) => {
        try {
          if (
            navigator &&
            navigator.clipboard &&
            navigator.clipboard.writeText
          ) {
            await navigator.clipboard.writeText(text)
          } else {
            const ele = document.createElement('textarea')
            ele.value = text
            document.body.appendChild(ele)
            ele.select()
            document.execCommand('copy')
            document.body.removeChild(ele)
          }
          setIsDevCopied(true)
          setTimeout(() => setIsDevCopied(false), 1000)
        } catch {}
      }
      const buyHref =
        normChain === 964
          ? `https://taodex.hotkeyswap.com/swap?inputCurrency=&outputCurrency=${contractAddress}`
          : buildBuyLink(normChain, normAddr, versionType)
      return (
        <article className="claim-card w-full block p-4 sm:p-6 bg-[#171a21] text-[#f8ffe8] border border-[#2a2d36] rounded-lg shadow-md transition-all hover:border-[#a135bb] hover:shadow-[0_0_16px_rgba(161,53,187,0.18)] hover:-translate-y-[1px] focus-within:border-[#a135bb]">
          <div className="grid grid-cols-1 md:grid-cols-[72px_minmax(0,1fr)_minmax(0,260px)] gap-3 sm:gap-4 items-start">
            {/* Col 1: Token avatar */}
            <div className="flex md:block justify-start">
              <TokenLogoImage
                address={contractAddress}
                chainId={chainId}
                version={versionType}
                src={logoUrl}
                loading="lazy"
                decoding="async"
                fetchpriority="low"
                alt={`${tokenName} logo`}
                width={64}
                height={64}
                sizes="(min-width: 640px) 64px, 52px"
                className="rounded-lg object-cover w-[52px] h-[52px] sm:w-[64px] sm:h-[64px] flex-shrink-0 bg-[#22252f] transition-opacity duration-200 opacity-0"
                onLoad={e => {
                  e.currentTarget.style.opacity = '1'
                }}
              />
            </div>

            {/* Col 2: Details (name + ticker, chain chip, dev/bonded, description) */}
            <div className="flex-1 min-w-0">
              {/* Header row: name, chain chip */}
              <div className="flex items-start gap-2 sm:gap-3">
                <h3 className="font-extrabold text-[18px] sm:text-[20px] tracking-tight truncate">
                  {tokenName}{' '}
                  <span className="text-[#8f8f8f]">({tokenSymbol})</span>
                </h3>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#22252f] text-[11px] flex-shrink-0">
                  <img
                    src={blockchainLogoUrl}
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                    alt={`${chainNames[chainId]} logo`}
                    width="16"
                    height="16"
                    className="w-4 h-4 bg-[#22252f] rounded-sm transition-opacity duration-200 opacity-0"
                    onLoad={e => {
                      e.currentTarget.style.opacity = '1'
                    }}
                    onError={e => {
                      const el = e.currentTarget as HTMLImageElement
                      if (!el.dataset.fallback) {
                        el.dataset.fallback = '1'
                        el.src = '/img/chain.png'
                      }
                    }}
                  />
                  <span className="text-[#cfcfcf]">{chainNames[chainId]}</span>
                </span>
              </div>

              {/* Sub-row: status chip + (optional) dev link and copy */}
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold tracking-wide ${
                    lpCreated
                      ? 'bg-[#7CFC98]/15 text-[#7CFC98]'
                      : 'bg-[#ffd700]/15 text-[#ffd700]'
                  }`}
                >
                  {lpCreated ? 'bonded' : 'launching'}
                </span>
                {devAddress && (
                  <span className="inline-flex items-center gap-1">
                    <Link
                      to={`/profile/?address=${String(
                        devAddress
                      ).toLowerCase()}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#22252f] text-[#cfcfcf] hover:text-[#f8ffe8]"
                      title={devAddress}
                    >
                      {isDevCopied ? (
                        <span className="text-[#7CFC98] font-semibold">
                          copied
                        </span>
                      ) : (
                        <>
                          <span className="opacity-80">dev:</span>
                          {devAddress.slice(0, 6)}...{devAddress.slice(-4)}
                        </>
                      )}
                    </Link>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#2a2d36] bg-[#171a21] text-[#cfcfcf] hover:bg-[#2a2d36] hover:border-[#a135bb]"
                      onClick={() => copyDev(String(devAddress))}
                      aria-label="Copy developer address"
                      title={isDevCopied ? 'Copied' : 'Copy developer address'}
                    >
                      <img src={CopyIcon} alt="" className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="mt-2 text-[12px] sm:text-[13px] text-[#f8ffe8]/80 line-clamp-2">
                {tokenDescription}
              </p>
              {/* Socials (moved from col 3) */}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px]">
                {website && (
                  <a
                    href={website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[12px] text-[#f500ff] hover:underline"
                  >
                    website
                  </a>
                )}
                {twitter && (
                  <a
                    href={twitter}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[12px] text-[#f500ff] hover:underline"
                  >
                    x
                  </a>
                )}
                {telegram && (
                  <a
                    href={telegram}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[12px] text-[#f500ff] hover:underline"
                  >
                    tg
                  </a>
                )}
              </div>
            </div>

            {/* Col 3: Metrics & actions */}
            <div className="flex flex-col gap-3 md:items-end">
              <div className="rounded-lg bg-[#1d2028] px-3 py-2 w-full md:w-auto">
                <div className="text-[11px] text-[#f8ffe8]/60">market cap</div>
                <div className="text-[14px] font-bold">
                  $
                  {Number(marketCap || 0).toLocaleString(undefined, {
                    maximumFractionDigits: 0
                  })}
                </div>
              </div>

              <div className="flex flex-wrap md:justify-end items-center gap-2">
                <a
                  href={buyHref}
                  className="inline-flex items-center justify-center px-3 py-1.5 text-[13px] font-bold rounded-lg text-black bg-[#7CFC98] hover:bg-[#b1ffcb] transition-all"
                  target={normChain === 964 ? '_blank' : undefined}
                  rel={normChain === 964 ? 'noreferrer' : undefined}
                  onClick={e => {
                    const href = buyHref
                    if (href.startsWith('http')) {
                      e.preventDefault()
                      window.open(href, '_blank', 'noopener,noreferrer')
                    }
                  }}
                >
                  view coin page
                </a>

                {isConnected &&
                  address &&
                  String(address).toLowerCase() ===
                    String(devAddress).toLowerCase() && (
                    <Link
                      to={`/edit/?chain=${normChain}&address=${encodeURIComponent(
                        normAddr
                      )}`}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#2a2d36] bg-[#171a21] text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] text-[13px] transition-all"
                      onClick={() => {
                        try {
                          const key = `editPrefill:${normChain}:${String(
                            contractAddress
                          ).toLowerCase()}`
                          const payload = {
                            name: tokenName,
                            symbol: tokenSymbol,
                            logoUrl,
                            chainId: normChain,
                            chainName: chainNames[normChain],
                            chainLogo: blockchainLogoUrl,
                            contract: String(contractAddress).toLowerCase(),
                            devAddress: String(devAddress || '').toLowerCase()
                          }
                          localStorage.setItem(key, JSON.stringify(payload))
                          localStorage.setItem(
                            'editPrefill:last',
                            JSON.stringify(payload)
                          )
                        } catch {}
                      }}
                    >
                      edit agent info
                    </Link>
                  )}
              </div>
            </div>
          </div>
        </article>
      )
    }
  )

  // ProfileHeader component
  /* 
  const ProfileHeader = () => {
    const shortAddr = effectiveAddress
      ? `${String(effectiveAddress).slice(0, 6)}...${String(effectiveAddress).slice(-4)}`
      : ''
    return (
      <section className="w-full rounded-lg border border-[#2a2d36] bg-[#171a21] text-[#f8ffe8] p-4 md:p-6 mb-6 flex items-start gap-4">
        <img
          src={userAvatarUrl}
          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
          onError={(event) => {
            const el = event.currentTarget as HTMLImageElement
            if (!el.dataset.fallback) {
              el.dataset.fallback = '1'
              el.src = "/img/moonboy67.png"
            }
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[20px] md:text-[24px] font-extrabold tracking-tight">{userName}</h1>
            {effectiveAddress && (
              <button
                onClick={copyAddress(effectiveAddress)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-[#2a2d36] bg-[#171a21] text-[12px] text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-all"
                aria-label="copy profile address"
                title={isTooltipDisplayed ? 'Copied' : 'Copy address'}
              >
                <span className="font-mono">{shortAddr}</span>
                <img src={CopyIcon} alt="copy" className="w-3.5 h-3.5" />
              </button>
            )}
            {profileAddress === address && address && (
              <Link
                to="/profile/edit"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-[#2a2d36] bg-[#171a21] text-[12px] text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-all"
                title="edit profile"
              >
                <EditIcon className="w-4 h-4" />
                <span>edit profile</span>
              </Link>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px]">
            {userWebsite && <a className="text-[#f500ff] hover:underline" href={userWebsite} target="_blank" rel="noreferrer">website</a>}
            {userX && <a className="text-[#f500ff] hover:underline" href={userX} target="_blank" rel="noreferrer">x</a>}
            {userTelegram && <a className="text-[#f500ff] hover:underline" href={userTelegram} target="_blank" rel="noreferrer">tg</a>}
          </div>
        </div>
      </section>
    )
  } */

  useEffect(() => {
    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason: any = ev?.reason
      const n = normalizeError(reason)
      if (n.kind !== 'abort')
        toastOnce('error', n.message || 'Unexpected error')
    }
    window.addEventListener('unhandledrejection', onRejection)
    return () => window.removeEventListener('unhandledrejection', onRejection)
  }, [])

  return (
    <div>
      <div className="GlobalContainer">
        <div style={{ zIndex: 1 }}>
          <TopBar />
          <div className="max-w-8xl m-auto px-3 pt-32 md:pt-12 pb-16 md:pb-20">
            {/* <div
              className="profile-card"
              style={{
                backgroundImage: `url(${userBannerUrl})`,
                backgroundSize: 'auto 192px',
                backgroundPosition: 'center top',
                backgroundRepeat: 'no-repeat',
                overflowX: 'hidden'
              }}
            >
              <div className="launchpad-eth-logo-container relative">
                <img src={userAvatarUrl} className="profile-avatar"
                  onError={event => {
                    event.target.src = "/img/moonboy67.png"
                    event.onerror = null
                  }} />
              </div>
              <div style={{ paddingTop: '72px' }} />
              <p className="profile-name absolute">
                {userName}{' '}
                {profileAddress === address && address && (
                  <button
                    onClick={handleEditIconClick}
                    style={{ all: 'unset', cursor: 'pointer' }}
                  >
                    <EditIcon className="profile-edit" />
                  </button>
                )}
              </p>
            </div> */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Link
                  to="/"
                  aria-label="Go back to home"
                  className="inline-flex items-center gap-1 text-[#f500ff] hover:text-[#f8ffe8] hover:underline"
                >
                  ← go back
                </Link>
              </div>
            </div>
            {/* <ProfileHeader /> */}
            <div className="flex flex-col md:flex-row items-start gap-3 w-full">
              <section className="ProfileBox mt-0 grow w-full min-w-0 rounded-lg border border-[#2a2d36] bg-[#171a21] text-[#f8ffe8] p-4 md:p-6 shadow-[0_0_8px_rgba(245,0,255,0.08)] hover:shadow-[0_0_16px_rgba(245,0,255,0.14)] hover:border-[#383c46] transition-all">
                <div className="w-full text-[18px] md:text-[20px] font-bold tracking-tight text-[#f8ffe8] mb-2">
                  profile
                </div>
                {effectiveAddress && (
                  <div className="-mt-1 mb-2 inline-flex items-center gap-2 text-[12px] text-[#f8ffe8]/80">
                    <span
                      className="font-mono px-2 py-0.5 rounded-lg border border-[#2a2d36] bg-[#171a21]"
                      title={String(effectiveAddress)}
                    >
                      {`${String(effectiveAddress).slice(0, 6)}...${String(
                        effectiveAddress
                      ).slice(-4)}`}
                    </span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-[#2a2d36] bg-[#171a21] hover:bg-[#2a2d36] hover:border-[#a135bb]"
                      onClick={copyAddress(effectiveAddress, 'profileTitle')}
                      aria-label="Copy profile address"
                      title={isProfileAddrCopied ? 'Copied' : 'Copy address'}
                    >
                      <img src={CopyIcon} alt="" className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">
                        {isProfileAddrCopied ? 'copied' : 'copy'}
                      </span>
                    </button>
                  </div>
                )}
                <div className="h-px bg-[#2a2d36]/60 -mx-2 md:-mx-4 mb-4" />
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <nav
                    className="inline-flex items-center rounded-lg border border-[#2a2d36] bg-[#171a21] p-1 overflow-x-auto"
                    role="tablist"
                    aria-label="dashboard sections"
                    aria-orientation="horizontal"
                    ref={tabListRef}
                    onKeyDown={e => {
                      if (e.key === 'ArrowRight') {
                        e.preventDefault()
                        focusNextInList(e.currentTarget, 1)
                      }
                      if (e.key === 'ArrowLeft') {
                        e.preventDefault()
                        focusNextInList(e.currentTarget, -1)
                      }
                    }}
                  >
                    <button
                      type="button"
                      className={`rounded-lg border px-3 py-1.5 font-medium tracking-wide text-[13px] transition-colors hover:translate-y-[-1px] ${
                        selectedOption === 'balances'
                          ? 'border-[#a135bb] bg-[#2a2d36] text-[#f8ffe8]'
                          : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/70 hover:bg-[#2a2d36] hover:border-[#a135bb]'
                      }`}
                      id="tab-balances"
                      role="tab"
                      aria-selected={selectedOption === 'balances'}
                      tabIndex={selectedOption === 'balances' ? 0 : -1}
                      aria-controls="panel-balances"
                      onClick={() => handleSelect('balances')}
                      onMouseEnter={() => warmBalancesCache(effectiveAddress)}
                    >
                      balances
                    </button>
                    <button
                      type="button"
                      className={`rounded-lg border px-3 py-1.5 font-medium tracking-wide text-[13px] transition-colors hover:translate-y-[-1px] ${
                        selectedOption === 'trades'
                          ? 'border-[#a135bb] bg-[#2a2d36] text-[#f8ffe8]'
                          : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/70 hover:bg-[#2a2d36] hover:border-[#a135bb]'
                      } ml-1`}
                      id="tab-trades"
                      role="tab"
                      aria-selected={selectedOption === 'trades'}
                      tabIndex={selectedOption === 'trades' ? 0 : -1}
                      aria-controls="panel-trades"
                      onClick={() => handleSelect('trades')}
                      onMouseEnter={() => warmTradesCache(effectiveAddress)}
                    >
                      trades
                    </button>
                    <button
                      type="button"
                      className={`rounded-lg border px-3 py-1.5 font-medium tracking-wide text-[13px] transition-colors hover:translate-y-[-1px] ${
                        selectedOption === 'coins'
                          ? 'border-[#a135bb] bg-[#2a2d36] text-[#f8ffe8]'
                          : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/70 hover:bg-[#2a2d36] hover:border-[#a135bb]'
                      } ml-1`}
                      id="tab-coins"
                      role="tab"
                      aria-selected={selectedOption === 'coins'}
                      tabIndex={selectedOption === 'coins' ? 0 : -1}
                      aria-controls="panel-coins"
                      onClick={() => handleSelect('coins')}
                      onMouseEnter={() => warmCoinsCache(effectiveAddress)}
                    >
                      coins
                    </button>
                  </nav>
                </div>
                <div
                  ref={contentShellRef}
                  className="relative transition-[min-height] duration-200 ease-out"
                  style={contentMinH ? { minHeight: contentMinH } : undefined}
                >
                  <div
                    id="panel-balances"
                    role="tabpanel"
                    aria-labelledby="tab-balances"
                    aria-hidden={selectedOption !== 'balances'}
                    className={`transition-opacity duration-200 ${
                      selectedOption === 'balances'
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none absolute inset-0'
                    }`}
                  >
                    <div aria-busy={loadingBalances}>
                      {loadingBalances ? (
                        <div
                          className="space-y-2"
                          role="status"
                          aria-busy="true"
                        >
                          {Array.from({ length: 6 }).map((_, i) => (
                            <BalanceRowSkeleton key={`bal-skel-${i}`} />
                          ))}
                        </div>
                      ) : chadBalances.length > 0 ? (
                        <ul aria-label="balances" className="space-y-2">
                          {chadBalances.map(
                            ({
                              chainId,
                              name,
                              symbol,
                              tokenAddress,
                              logoUrl,
                              balance,
                              versionType
                            }) => (
                              <li
                                key={`${chainId}-${tokenAddress}`}
                                className="rounded-lg transition-colors border border-transparent"
                              >
                                <Suspense fallback={<BalanceRowSkeleton />}>
                                  <BalanceCard
                                    chainId={chainId}
                                    name={name}
                                    symbol={symbol}
                                    tokenAddress={tokenAddress}
                                    logoUrl={logoUrl}
                                    balance={balance}
                                    versionType={versionType}
                                  />
                                </Suspense>
                              </li>
                            )
                          )}
                        </ul>
                      ) : (
                        <div className="claim-card w-full p-4 text-center text-sm text-[#f8ffe8]/70 bg-[#171a21] border border-[#2a2d36] rounded-lg">
                          no data yet
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    id="panel-trades"
                    role="tabpanel"
                    aria-labelledby="tab-trades"
                    aria-hidden={selectedOption !== 'trades'}
                    className={`transition-opacity duration-200 ${
                      selectedOption === 'trades'
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none absolute inset-0'
                    }`}
                  >
                    <div aria-busy={loadingTrades}>
                      <div className="rounded-lg border border-[#2a2d36] bg-[#2a2d36]/20 px-3 py-2 mb-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px] text-[#f8ffe8]/60">
                          <p className="Balance-token-name left-aligned">
                            token
                          </p>
                          <p className="Balance-token-name left-aligned">
                            type
                          </p>
                          <p className="Balance-token-name left-aligned">
                            amount
                          </p>
                          <p className="Balance-token-name left-aligned">
                            date
                          </p>
                        </div>
                      </div>
                      {loadingTrades ? (
                        <div
                          className="space-y-2"
                          role="status"
                          aria-busy="true"
                        >
                          {Array.from({ length: 8 }).map((_, i) => (
                            <TradeRowSkeleton key={`trade-skel-${i}`} />
                          ))}
                        </div>
                      ) : history[0].name !== '' ? (
                        <ul aria-label="trades" className="flex flex-col">
                          {history.map(
                            (
                              { chainId, amount, name, token, type, date },
                              i
                            ) => (
                              <li
                                key={`${chainId}-${token}-${type}-${i}`}
                                className="rounded-lg transition-colors border border-transparent"
                              >
                                <Suspense fallback={<TradeRowSkeleton />}>
                                  <TradeCard
                                    name={name}
                                    amount={amount}
                                    token={token}
                                    type={type}
                                    date={date}
                                    scanLink={scanLinks[chainId]}
                                    coinName={coinNames[chainId]}
                                  />
                                </Suspense>
                              </li>
                            )
                          )}
                        </ul>
                      ) : (
                        <div className="claim-card w-full p-4 text-center text-sm text-[#f8ffe8]/70 bg-[#171a21] border border-[#2a2d36] rounded-lg">
                          no trades yet
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    id="panel-coins"
                    role="tabpanel"
                    aria-labelledby="tab-coins"
                    aria-hidden={selectedOption !== 'coins'}
                    className={`transition-opacity duration-200 ${
                      selectedOption === 'coins'
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none absolute inset-0'
                    }`}
                  >
                    <div aria-busy={loadingCoins}>
                      {loadingCoins ? (
                        <div
                          className="grid grid-cols-1 gap-4 w-full px-0"
                          role="status"
                          aria-busy="true"
                        >
                          {Array.from({ length: 6 }).map((_, i) => (
                            <div
                              key={`coin-skel-${i}`}
                              className="w-full self-center"
                            >
                              <CoinCardSkeleton />
                            </div>
                          ))}
                        </div>
                      ) : chadLists.length > 0 ? (
                        <div className="grid grid-cols-1 gap-4 w-full px-0">
                          {chadLists.map(
                            ({
                              chainId,
                              progress,
                              Liquidity,
                              tokenName,
                              tokenSymbol,
                              tokenDescription,
                              logoUrl,
                              bannerUrl,
                              address: chadAddress,
                              depositedAmount,
                              contractAddress,
                              devAddress,
                              dexName,
                              marketCap,
                              website,
                              twitter,
                              telegram,
                              blockchainLogoUrl,
                              lpCreated,
                              lastAction,
                              lastActionNumber,
                              versionType
                            }) =>
                              tokenName !== '' ? (
                                <div
                                  key={`${chainId}-${String(
                                    contractAddress
                                  ).toLowerCase()}`}
                                  className="flex flex-col w-full self-center"
                                >
                                  <CoinCard
                                    chainId={chainId}
                                    tokenName={tokenName}
                                    tokenSymbol={tokenSymbol}
                                    tokenDescription={tokenDescription}
                                    logoUrl={logoUrl}
                                    chadAddress={chadAddress}
                                    contractAddress={contractAddress}
                                    devAddress={devAddress}
                                    marketCap={marketCap}
                                    Liquidity={Liquidity}
                                    website={website}
                                    twitter={twitter}
                                    telegram={telegram}
                                    blockchainLogoUrl={blockchainLogoUrl}
                                    lpCreated={lpCreated}
                                    lastAction={lastAction}
                                    lastActionNumber={lastActionNumber}
                                    toggleWatchlist={toggleWatchlist}
                                    watchlist={watchlist}
                                    versionType={versionType}
                                  />
                                </div>
                              ) : null
                          )}
                        </div>
                      ) : (
                        <div className="claim-card w-full p-4 text-center text-sm text-[#f8ffe8]/70 bg-[#171a21] border border-[#2a2d36] rounded-lg">
                          no coins yet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
              <aside
                className={`flex self-start h-fit flex-col bg-[#171a21] rounded-[12px] p-6 md:w-[360px] w-full border border-[#2a2d36] text-[#f8ffe8] md:sticky`}
              >
                <div className="flex flex-col gap-3 w-full">
                  <div className="text-xs tracking-wide text-[#f8ffe8]/60">
                    referral reward
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-[32px] leading-none font-extrabold text-[#f8ffe8]">
                      0.5%
                    </div>
                  </div>
                  <p className="text-sm text-[#f8ffe8]/80">
                    earn <span className="font-semibold">0.5%</span> of each
                    trade using your referral link.
                  </p>
                  <div className="rounded-[10px] border border-[#2a2d36] bg-[#171a21] px-3 py-2 flex items-center gap-2 w-full">
                    <span
                      className="text-[12px] text-[#f8ffe8] truncate flex-1 min-w-0 relative"
                      style={{ display: 'inline-block' }}
                    >
                      <span
                        style={{
                          visibility: isReferralCopied ? 'hidden' : 'visible',
                          display: 'inline-block',
                          width: '100%'
                        }}
                      >
                        {referlink}
                      </span>
                      <span
                        style={{
                          visibility: isReferralCopied ? 'visible' : 'hidden',
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        copied
                      </span>
                      <span className="sr-only" aria-live="polite">
                        {isReferralCopied ? 'Copied' : ''}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="inline-flex justify-center items-center rounded-[8px] border border-[#2a2d36] bg-[#2a2d36]/40 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-colors hover:translate-y-[-1px] w-9 h-8 min-w-[36px]"
                      onClick={copyAddress(referlink, 'referral')}
                      aria-label="Copy referral link to clipboard"
                    >
                      {isReferralCopied ? (
                        // inline checkmark to avoid image loading jitter
                        <svg
                          viewBox="0 0 20 20"
                          width="16"
                          height="16"
                          aria-hidden="true"
                          className="w-4 h-4"
                        >
                          <path
                            fill="currentColor"
                            d="M7.7 13.3l-3-3a1 1 0 111.4-1.4l2.3 2.3 5.5-5.5a1 1 0 111.4 1.4l-6.2 6.2a1 1 0 01-1.4 0z"
                          />
                        </svg>
                      ) : (
                        <img
                          src={CopyIcon}
                          alt=""
                          aria-hidden="true"
                          decoding="async"
                          loading="lazy"
                          fetchpriority="low"
                          width="16"
                          height="16"
                          className="w-4 h-4"
                        />
                      )}
                    </button>
                  </div>
                </div>
              </aside>
            </div>
            {/* <div
              className="grid xl:grid-cols-1 gap-4"
              style={{ marginTop: '20px' }}
            >
              <div className="flex flex-col gap-3">
                <div
                  className="flex flex-col-reverse lg:flex-row bg-[#FFC000] rounded-[40px] lg:px-8 px-2.5 py-5"
                  style={{
                    boxShadow: 'rgb(103, 103, 103) 0px 5px 10px 0px',
                    border: '2px solid white',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-around'
                  }}
                >
                  <div
                    style={{
                      textAlign: 'center',
                      alignItems: 'center',
                      justifyContent: 'center',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      className="text-xl mb-1.5 refer-heading"
                      style={{
                        width: '80%',
                        textAlign: 'center',
                        fontSize: '35px',
                        fontWeight: 'black',
                        textTransform: 'uppercase',
                        fontWeight: 'bold'
                      }}
                    >
                      Refer HOTKEY to your friends and start earning
                    </div>
                    <div
                      className="Text1"
                      style={{
                        width: '100%',
                        fontSize: '15px',
                        color: 'black'
                      }}
                    >
                      Introduce your friends to a better way to trade. Refer
                      them to Blackoump.fun, and you will get{' '}
                      <span className='fees-bold'>
                        50% of the fees
                      </span>
                    </div>
                  </div>
                  <div>
                    <img style={{ height: '180px' }} src={footericon} />
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default Profile
